import {
    ACTIVE_VIEW,
    DEFAULT_FILTER_TYPES,
    FILTERS,
    useFiltersStore,
} from "@/Stores/filters";
import { computed, isRef } from "vue";
import { usePage } from "@inertiajs/vue3";
import { FilterType } from "@/SharedComponents/Filters";
import { useSelectedRows } from "@/Composables/useSelectedRows";
import { addDateFilterShortcutRanges } from "@/Utils/filters";
import { includeHours } from "@/Utils/time";
import { toUTC } from "@/Utils/date";
import { isDate } from "date-fns";
import { uniq } from "lodash";

function _toUTC(date) {
    return date
        ? toUTC(isDate(date) ? includeHours(date).toISOString() : date)
        : null;
}

export function useFilters(storeKey) {
    const hasFilterStore = computed(() => !!filtersStore[_storeKey.value]);

    const _storeKey = computed(() =>
        isRef(storeKey) ? storeKey.value : storeKey
    );

    const filtersStore = useFiltersStore();

    const { handleClearSelection } = useSelectedRows(_storeKey);

    const handleSwitchView = (view) => {
        if (hasFilterStore.value) {
            filtersStore[_storeKey.value][ACTIVE_VIEW] = view;
        }
        handleClearSelection();
    };

    const handleInitialiseFilters = (filterStates, defaultFilters = []) => {
        if (!hasFilterStore.value) return;

        filtersStore[_storeKey.value][DEFAULT_FILTER_TYPES] = defaultFilters;
        filtersStore[_storeKey.value][FILTERS] = addDateFilterShortcutRanges(
            Object.fromEntries(
                Object.entries(filterStates).filter(([, v]) =>
                    Array.isArray(v) ? v.length !== 0 : v
                )
            )
        );
    };

    const handleChangeFilter = (filterType, filterState) => {
        if (!filtersStore[_storeKey.value]) {
            console.error(
                "The specified store key is not present in the filters store."
            );
        }

        if (!filterState) {
            delete filtersStore[_storeKey.value][FILTERS][filterType];
            return;
        }

        if (filterType === "linked_to") {
            filterState.children = uniq(filterState.children);
        }

        if (filterType === "asset_groups") {
            filterState = uniq(filterState.children);
        }

        filtersStore[_storeKey.value][FILTERS][filterType] = filterState;
    };

    function handleClearFilters(...filterTypes) {
        // If no specific filterTypes are given, clear all currently
        // applied filters, apart from the default ones required by a view.
        if (filterTypes.length === 0) {
            filterTypes = Object.keys(customFilterStates.value);
        }

        filterTypes.forEach((filterType) => {
            delete filtersStore[_storeKey.value][FILTERS][filterType];
        });
    }

    const filterStates = computed(() => {
        return filtersStore[_storeKey.value][FILTERS];
    });

    const customFilterStates = computed(() => {
        // Remove Linked To when the children and the buildings are empty.
        if (
            filtersStore[_storeKey.value][FILTERS].linked_to &&
            !filtersStore[_storeKey.value][FILTERS].linked_to.children.length &&
            !filtersStore[_storeKey.value][FILTERS].linked_to.buildings?.length
        ) {
            delete filtersStore[_storeKey.value][FILTERS].linked_to;
        }

        return Object.fromEntries(
            Object.entries(filtersStore[_storeKey.value][FILTERS]).filter(
                ([key]) => {
                    return (
                        Object.entries(
                            filtersStore[_storeKey.value][FILTERS][key]
                        ).length > 0 &&
                        !filtersStore[_storeKey.value][
                            DEFAULT_FILTER_TYPES
                        ].includes(key)
                    );
                }
            )
        );
    });

    const isFiltered = computed(() => {
        return Object.keys(filterValues.value).length !== 0;
    });

    const filterValues = computed(() => {
        const transform = (key, value) => {
            if (key === FilterType.STATUS) {
                return {
                    status: value,
                };
            }

            if (key === FilterType.ASSET_GROUPS) {
                return {
                    asset_groups: value,
                };
            }

            if (key === FilterType.LINKED_TO) {
                return {
                    space: value.children,
                    tenant: value.buildings,
                };
            }

            if (key === FilterType.ASSIGNED_TO) {
                return {
                    assignee: value.map((assigneeId) =>
                        assigneeId !== "user"
                            ? assigneeId
                            : usePage().props.auth.user.id
                    ),
                };
            }

            if (key === FilterType.EXPIRATION_DATE) {
                const { range } = value;

                if (!range) {
                    return;
                }

                return {
                    expiration_date: {
                        gte: _toUTC(range.start),
                        lte: _toUTC(range.end),
                    },
                };
            }

            if (key === FilterType.NOTIFICATION_DATE) {
                const { range } = value;

                if (!range) {
                    return;
                }

                return {
                    notification_date: {
                        gte: _toUTC(range.start),
                        lte: _toUTC(range.end),
                    },
                };
            }

            if (key === FilterType.DUE_DATE) {
                const { range } = value;

                if (!range) {
                    return;
                }

                if (!range.start && range.end) {
                    return {
                        overdue_only: true,
                    };
                }

                return {
                    scheduled_for: {
                        gte: _toUTC(range.start),
                        lte: _toUTC(range.end),
                    },
                };
            }

            if (key === FilterType.COMPLETED_DATE) {
                const { range } = value;

                if (!range) {
                    return;
                }

                return {
                    completed_at: {
                        gte: _toUTC(range.start),
                        lte: _toUTC(range.end),
                    },
                };
            }

            if (key === FilterType.CREATED_DATE) {
                const { range } = value;

                if (!range) {
                    return;
                }

                return {
                    created_at: {
                        gte: _toUTC(range.start),
                        lte: _toUTC(range.end),
                    },
                };
            }

            if (key === FilterType.PURCHASED_DATE) {
                const { range } = value;

                if (!range) {
                    return;
                }

                return {
                    purchased_at: {
                        gte: _toUTC(range.start),
                        lte: _toUTC(range.end),
                    },
                };
            }

            if (key === FilterType.VISIBILITY) {
                return {
                    visibility: value,
                };
            }

            if (key === FilterType.TASK_TYPE) {
                return {
                    task_type: value,
                };
            }

            if (key === FilterType.PRIORITY) {
                return {
                    priority: value,
                };
            }

            if (key === FilterType.CATEGORY) {
                return {
                    categories: value,
                };
            }

            if (key === FilterType.BRAND) {
                return {
                    brands: value,
                };
            }

            if (key === FilterType.SUPPLIER) {
                return {
                    suppliers: value,
                };
            }

            if (key === FilterType.TAGS) {
                return {
                    tags: value,
                };
            }

            if (key === FilterType.RECURRENCE) {
                return {
                    intervals: value,
                };
            }
        };

        const transformedFilters = Object.keys(
            filtersStore[_storeKey.value][FILTERS]
        ).map((filterType) => {
            return transform(
                filterType,
                filtersStore[_storeKey.value][FILTERS][filterType]
            );
        });

        return transformedFilters.reduce(
            (filters, filter) => ({ ...filters, ...filter }),
            {}
        );
    });

    const defaultFilterTypes = computed(() => {
        return filtersStore[_storeKey.value][DEFAULT_FILTER_TYPES];
    });

    const activeView = computed(() => {
        return filtersStore[_storeKey.value][ACTIVE_VIEW];
    });

    return {
        handleSwitchView,
        handleInitialiseFilters,
        handleChangeFilter,
        handleClearFilters,
        customFilterStates,
        filterStates,
        filterValues,
        isFiltered,
        defaultFilterTypes,
        activeView,
    };
}
